import { domMax, m } from 'framer-motion';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CapiPlp, GridSize } from 'types/capi/plp';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import FramerMotion from '~/components/FramerMotion';
import ProductCard from '~/components/ProductCard';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';

import ImageTile from '../ImageTile';
import { MidModuleSection } from '../ModuleSection';
import Pagination from './Pagination';

const MODULE_TYPE = 'plp-main-list';

const gridStyles = {
  large: 'grid-cols-1 md:grid-cols-3 ',
  small: 'grid-cols-2 md:grid-cols-4'
};

type ImageTileConfig = {
  column: number;
  field:
    | 'image_1_section_modules'
    | 'image_2_section_modules'
    | 'image_3_section_modules'
    | 'image_4_section_modules';
  mobileProductsBefore: number;
  productsBeforeRow: number;
  slot: 1 | 2 | 3 | 4;
};

const columns: Record<GridSize, number> = { large: 3, small: 4 };

export const imageTiles: Record<GridSize, ImageTileConfig[]> = {
  large: [
    {
      column: 2,
      field: 'image_1_section_modules',
      mobileProductsBefore: 2,
      productsBeforeRow: 0,
      slot: 1
    },
    {
      column: 1,
      field: 'image_2_section_modules',
      mobileProductsBefore: 9,
      productsBeforeRow: 11,
      slot: 2
    },
    {
      column: 2,
      field: 'image_3_section_modules',
      mobileProductsBefore: 16,
      productsBeforeRow: 25,
      slot: 3
    },
    {
      column: 1,
      field: 'image_4_section_modules',
      mobileProductsBefore: 25,
      productsBeforeRow: 39,
      slot: 4
    }
  ],
  small: [
    {
      column: 3,
      field: 'image_1_section_modules',
      mobileProductsBefore: 4,
      productsBeforeRow: 0,
      slot: 1
    },
    {
      column: 1,
      field: 'image_2_section_modules',
      mobileProductsBefore: 14,
      productsBeforeRow: 12,
      slot: 2
    },
    {
      column: 3,
      field: 'image_3_section_modules',
      mobileProductsBefore: 24,
      productsBeforeRow: 28,
      slot: 3
    },
    {
      column: 1,
      field: 'image_4_section_modules',
      mobileProductsBefore: 36,
      productsBeforeRow: 44,
      slot: 4
    }
  ]
};

const TILE_ROWS = 2;

function blockStyle(row: number, column?: number) {
  return { '--tile-column': column, '--tile-row': row } as CSSProperties;
}

export function calculateBlockPlacement<
  T extends { productsBeforeRow: number }
>(blocks: T[], gridSize: GridSize, productCount: number) {
  const perRow = columns[gridSize];
  const productsBesideTile = TILE_ROWS * (perRow - 2);
  const placed: (T & { row: number })[] = [];
  let productsUsed = 0;
  let nextRow = 1;

  blocks
    .slice()
    .sort((a, b) => a.productsBeforeRow - b.productsBeforeRow)
    .forEach((block) => {
      const isTile = 'field' in block;
      const productRows = Math.max(
        placed.length ? 1 : 0,
        Math.round((block.productsBeforeRow - productsUsed) / perRow)
      );
      const productsNeeded =
        productRows * perRow + (isTile ? productsBesideTile : 0);

      if (productsUsed + productsNeeded > productCount) return;

      productsUsed += productsNeeded;
      nextRow += productRows;
      placed.push({ ...block, row: nextRow });
      nextRow += isTile ? TILE_ROWS : 1;
    });

  return placed;
}

const ROWS_BEFORE_FEATURE = 2;

const mobileColumns: Record<GridSize, number> = { large: 1, small: 2 };

export function placeFeatureRow<T extends { row: number }>(
  blocks: T[],
  gridSize: GridSize,
  productCount: number
) {
  const perRow = columns[gridSize];
  const tileCells = blocks.some((block) => 'field' in block && block.row === 1)
    ? TILE_ROWS * 2
    : 0;
  const fillsRows = productCount >= ROWS_BEFORE_FEATURE * perRow - tileCells;
  const row = fillsRows
    ? ROWS_BEFORE_FEATURE + 1
    : Math.ceil(productCount / perRow) + 1;

  return {
    blocks: blocks.map((block) =>
      block.row >= row ? { ...block, row: block.row + 1 } : block
    ),
    mobileProductsBefore: Math.min(
      ROWS_BEFORE_FEATURE * mobileColumns[gridSize],
      productCount
    ),
    row
  };
}

const midBanner: Record<
  GridSize,
  { mobileProductsBefore: number; productsBeforeRow: number }
> = {
  large: { mobileProductsBefore: 18, productsBeforeRow: 19 },
  small: { mobileProductsBefore: 18, productsBeforeRow: 20 }
};

const imageTileStyles =
  'relative col-span-full -mx-4 aspect-[4/5] md:mx-0 md:aspect-auto md:col-span-2 md:row-span-2 md:[grid-column-start:var(--tile-column)] md:[grid-row-start:var(--tile-row)]';

const Products = ({
  gridSize = 'small',
  allowNotifications,
  feature,
  listId,
  modulePosition
}: {
  allowNotifications: boolean;
  feature?: ReactNode;
  gridSize: GridSize;
  listId: string;
  modulePosition: number;
}) => {
  const { q } = useQsParams();
  const plp = usePageData<CapiPlp>();
  const pageType = q ? 'search' : 'plp';

  const tiles = imageTiles[gridSize].filter(
    (tile) => !!plp[tile.field]?.length
  );

  const hasMidBanner = !!plp.mid_section_modules?.length;

  const blocks = calculateBlockPlacement(
    hasMidBanner
      ? [...tiles, { ...midBanner[gridSize], isBanner: true }]
      : tiles,
    gridSize,
    plp.products.length
  );

  const featureRow = feature
    ? placeFeatureRow(blocks, gridSize, plp.products.length)
    : undefined;

  const placedBlocks = featureRow?.blocks ?? blocks;

  const featureChild = featureRow && (
    <div
      className="col-span-full mx-[calc(50%-50vw)] md:[grid-row-start:var(--tile-row)]"
      key="plp-feature"
      style={blockStyle(featureRow.row)}
    >
      {feature}
    </div>
  );

  const midRow = placedBlocks.find((b) => 'isBanner' in b)?.row;

  const gridChildren: ReactNode[] = [];

  const pushTilesBefore = (index: number) =>
    placedBlocks
      .filter(
        (block) =>
          !('isBanner' in block) && block.mobileProductsBefore === index
      )
      .forEach((block) => {
        const tile = block as ImageTileConfig & { row: number };

        gridChildren.push(
          <ImageTile
            className={imageTileStyles}
            key={`plp-image-${tile.slot}`}
            modules={plp[tile.field]}
            slot={tile.slot}
            style={blockStyle(tile.row, tile.column)}
          />
        );
      });

  plp.products.forEach((product, index) => {
    if (midRow && index === midBanner[gridSize].mobileProductsBefore)
      gridChildren.push(
        <MidModuleSection
          className="md:[grid-row-start:var(--tile-row)]"
          key="plp-mid"
          style={blockStyle(midRow)}
        />
      );

    pushTilesBefore(index);

    if (featureRow?.mobileProductsBefore === index)
      gridChildren.push(featureChild);

    gridChildren.push(
      <FramerMotion feature={domMax} key={`plp-product-${index}`}>
        <m.div layout>
          {product.type === 'external' ? (
            <ProductCardExternal
              allowNotifications={allowNotifications}
              listId={listId}
              modulePosition={modulePosition}
              pageType={pageType}
              position={index + 1}
              product={product as ProductListItemExternal}
            />
          ) : (
            <ProductCardInternal
              allowNotifications={allowNotifications}
              gridSize={gridSize}
              listId={listId}
              modulePosition={modulePosition}
              pageType={pageType}
              position={index + 1}
              product={product as ProductListItemInternal}
            />
          )}
        </m.div>
      </FramerMotion>
    );
  });

  pushTilesBefore(plp.products.length);

  if (featureRow?.mobileProductsBefore === plp.products.length)
    gridChildren.push(featureChild);

  return (
    <div
      className={
        !q
          ? `py-6 desktop:w-desktop ${plp.products.length > 0 || !feature ? 'min-h-screen' : ''}`
          : 'py-6'
      }
    >
      {plp.products.length > 0 ? (
        <div className="flex flex-col gap-4">
          <div
            className={`grid gap-x-2 gap-y-4 md:gap-6 ${gridStyles[gridSize]}`}
          >
            {gridChildren}
          </div>
          <div className="mt-6">
            <Pagination pagination={plp.pagination} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <NoResultsMessage />
          {feature && <div className="mx-[calc(50%-50vw)]">{feature}</div>}
        </div>
      )}
    </div>
  );
};

function ProductCardInternal({
  product,
  allowNotifications,
  gridSize,
  listId,
  modulePosition,
  position,
  pageType
}: {
  allowNotifications: boolean;
  gridSize: GridSize;
  listId: string;
  modulePosition: number;
  pageType: 'plp' | 'search';
  position: number;
  product: ProductListItemInternal;
}) {
  function onProductCardClick(product: ProductListItem) {
    if (product.queryID) localStorage.setItem('queryID', product.queryID);
  }

  return (
    <ProductCard.Internal
      allowNotifications={allowNotifications}
      cardSize={gridSize}
      listId={listId}
      modulePosition={modulePosition}
      moduleType={MODULE_TYPE}
      position={position}
      padImage={true}
      product={product}
      imageLoading={position <= 10 ? 'eager' : 'lazy'}
      onClick={() => onProductCardClick(product)}
      pageType={pageType}
      pagePosition="PLP Main List"
    />
  );
}

function ProductCardExternal({
  product,
  allowNotifications,
  listId,
  modulePosition,
  position,
  pageType
}: {
  allowNotifications: boolean;
  listId: string;
  modulePosition: number;
  pageType: 'plp' | 'search';
  position: number;
  product: ProductListItemExternal;
}) {
  return (
    <ProductCard.External
      product={product}
      allowNotifications={allowNotifications}
      listId={listId}
      modulePosition={modulePosition}
      moduleType={MODULE_TYPE}
      position={position}
      padImage
      className="h-auto min-w-40"
      imageLoading="eager"
      cardSize="small"
      pagePosition="PLP Quick Buy"
      overrideInteractionPagePosition={true}
      pageType={pageType}
    />
  );
}

const NoResultsMessage = () => {
  const { t } = useTranslation('plp');

  return (
    <h2 className="text-caption-grey flex items-center justify-center py-6 text-center">
      {t('noResults')}
    </h2>
  );
};

export default Products;
